import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { Entities } from 'src/enums'
import { SensorQualityControlResult } from 'src/api'
import { useApiInstance } from './useApiInstance'

interface RequestParams {
  uniqueIds: string[]
  options?: Omit<UseQueryOptions<SensorQualityControlResult, Error>, 'queryKey' | 'queryFn'>
}

/**
 * Runs the quality control checks for the given sensors. Every item may be a sensor uniqueId,
 * an IMEI or a devEUI - unknown ids are silently skipped by the backend.
 *
 * The endpoint is a POST, but the call is idempotent and read-only, so it is modelled as a query
 * to get caching and loading states for free. It is disabled while there is nothing to check.
 */
export const useSensorQualityControlRequest = ({ uniqueIds, options }: RequestParams) => {
  const api = useApiInstance()

  return useQuery<SensorQualityControlResult, Error>(
    [Entities.Sensors, 'quality-control', JSON.stringify(uniqueIds)],
    async () => {
      const { data } = await api.v1AdminSensorsQualityControlPost({ uniqueIds })
      return data as unknown as SensorQualityControlResult
    },
    {
      enabled: uniqueIds.length > 0,
      ...options
    }
  )
}
